import { LoadingButton } from '@mui/lab';
import {
	Button,
	Card,
	CardContent,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	InputAdornment,
	inputBaseClasses,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

const SURVEY_KEY = 'aio_pro_survey_done';
const SURVEY_INTERVAL = 7 * 24 * 60 * 60 * 1000;
const GOOGLE_SCRIPT_URL =
	'https://docs.google.com/forms/d/e/1FAIpQLSfpzKw4jClM3x3JBT7MqNYVX2x8dcoT-757m9SQ5IS4Q_we-Q/formResponse';

type FormState = {
	email: string;
	plan: '$1' | '$2' | undefined;
};

const ProSurveyModal: React.FC = () => {
	const [open, setOpen] = useState(false);
	const [openForm, setOpenForm] = useState(false);
	const {
		handleSubmit,
		register,
		watch,
		formState: { isLoading, errors },
		setValue,
	} = useForm<FormState>({ defaultValues: { email: '' } });

	const onSubmit: SubmitHandler<FormState> = async (data) => {
		await handleAnswer('yes', `${data.email.trim()}@gmail.com`, data.plan);
	};

	const openEmailInput = (plan: '$1' | '$2') => {
		setOpenForm(true);
		setValue('plan', plan);
	};

	useEffect(() => {
		const stored = localStorage.getItem(SURVEY_KEY);
		if (stored) {
			try {
				const { timestamp, plan, answer, email } = JSON.parse(stored);
				const now = Date.now();
				if (answer === 'yes' && plan && email) {
					return;
				}
				if (now - timestamp >= SURVEY_INTERVAL) {
					setOpen(true);
				}
			} catch {
				// lỗi parse -> coi như chưa có survey
				setOpen(true);
			}
		} else {
			setOpen(true);
		}
	}, []);

	const handleAnswer = async (
		answer: 'yes' | 'no',
		email?: string,
		plan?: '$1' | '$2'
	) => {
		localStorage.setItem(
			SURVEY_KEY,
			JSON.stringify({ answer, plan, timestamp: Date.now(), email })
		);
		let locationData: { ip?: string; country?: string; city?: string } = {};

		const res = await fetch('https://ipwho.is/');
		if (res.ok) {
			const data = await res.json();
			locationData = {
				ip: data.ip,
				country: data.country,
				city: data.city,
			};
		}

		try {
			if (email && plan) {
				await fetch(GOOGLE_SCRIPT_URL, {
					method: 'POST',
					mode: 'no-cors',
					body: new URLSearchParams({
						'entry.747989305': email as string,
						'entry.1081439356': answer as string,
						'entry.1178300410': plan as string,
						'entry.1986655528': locationData.country as string,
						'entry.1515346817': locationData.city as string,
					}),
				});
			}
			toast('Thank you for taking the time to complete the survey.', {
				type: 'success',
			});
			setOpen(false);
		} catch (err) {
			console.error('Failed to send survey response:', err);
		}
	};

	return (
		<Dialog open={open} maxWidth="md" fullWidth>
			<DialogTitle>Survey for Pro plan 🚀</DialogTitle>
			<DialogContent dividers>
				<Grid container spacing={2}>
					{/* $1 Basic Plan */}
					<Grid item xs={12} md={6}>
						<Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
							<CardContent>
								<Typography variant="h6" gutterBottom>
									Basic Plan – $1/month
								</Typography>
								<Typography variant="body2" gutterBottom>
									Includes all core Pro features with limited subtitle
									translation.
								</Typography>
								<ul style={{ paddingLeft: '1.2rem', marginTop: '0.5rem' }}>
									<li>
										AI-powered subtitle translation with accurate movie context
									</li>
									<li>Faster subtitle processing with upgraded servers</li>
									<li>Direct subtitle integration with Stremio</li>
									<li>
										Access to Subscene & OpenSubtitles Pro APIs for more and
										higher-quality subtitles
									</li>
									<li>
										Notifications when new subtitles are available for your
										favorite movies
									</li>
									<li>Save and bulk download subtitles from your list</li>
								</ul>
								<Button
									fullWidth
									variant="contained"
									color="primary"
									sx={{ mt: 2 }}
									onClick={() => openEmailInput('$1')}>
									Choose Basic – $1/month
								</Button>
								{openForm && watch('plan') === '$1' && (
									<Stack
										direction="row"
										component="form"
										mt={2}
										spacing={2}
										onSubmit={handleSubmit(onSubmit)}>
										<TextField
											label="Email"
											variant="standard"
											size="small"
											error={!!errors.email}
											{...register('email', { required: true })}
											slotProps={{
												htmlInput: {
													sx: { textAlign: 'right' },
												},
												input: {
													autoComplete: 'off',
													endAdornment: (
														<InputAdornment
															position="end"
															sx={{
																alignSelf: 'flex-end',
																margin: 0,
																marginBottom: '5px',
																opacity: 0,
																pointerEvents: 'none',
																[`[data-shrink=true] ~ .${inputBaseClasses.root} > &`]:
																	{
																		opacity: 1,
																	},
															}}>
															@gmail.com
														</InputAdornment>
													),
												},
											}}
										/>
										<LoadingButton
											loading={isLoading}
											variant="contained"
											type="submit"
											color="primary">
											Submit
										</LoadingButton>
									</Stack>
								)}
							</CardContent>
						</Card>
					</Grid>

					{/* $2 Premium Plan */}
					<Grid item xs={12} md={6}>
						<Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
							<CardContent>
								<Typography variant="h6" gutterBottom>
									Premium Plan – $2/month
								</Typography>
								<Typography variant="body2" gutterBottom>
									All Pro features with extended subtitle translation and
									priority support.
								</Typography>
								<ul style={{ paddingLeft: '1.2rem', marginTop: '0.5rem' }}>
									<li>
										AI-powered subtitle translation with accurate movie context
									</li>
									<li>Faster subtitle processing with upgraded servers</li>
									<li>Direct subtitle integration with Stremio</li>
									<li>
										Access to Subscene & OpenSubtitles Pro APIs for more and
										higher-quality subtitles
									</li>
									<li>
										Notifications when new subtitles are available for your
										favorite movies
									</li>
									<li>Save and bulk download subtitles from your list</li>
									<li>
										<b>Extended translation limits</b>
									</li>
									<li>
										<b>Priority support</b>
									</li>
								</ul>
								<Button
									fullWidth
									variant="contained"
									color="secondary"
									sx={{ mt: 2 }}
									onClick={() => openEmailInput('$2')}>
									Choose Premium – $2/month
								</Button>
								{openForm && watch('plan') === '$2' && (
									<Stack
										direction="row"
										component="form"
										mt={2}
										spacing={2}
										onSubmit={handleSubmit(onSubmit)}>
										<TextField
											label="Email"
											variant="standard"
											size="small"
											error={!!errors.email}
											{...register('email', { required: true })}
											slotProps={{
												htmlInput: {
													sx: { textAlign: 'right' },
												},
												input: {
													autoComplete: 'off',
													endAdornment: (
														<InputAdornment
															position="end"
															sx={{
																alignSelf: 'flex-end',
																margin: 0,
																marginBottom: '5px',
																opacity: 0,
																pointerEvents: 'none',
																[`[data-shrink=true] ~ .${inputBaseClasses.root} > &`]:
																	{
																		opacity: 1,
																	},
															}}>
															@gmail.com
														</InputAdornment>
													),
												},
											}}
										/>
										<LoadingButton
											loading={isLoading}
											variant="contained"
											type="submit"
											color="secondary">
											Submit
										</LoadingButton>
									</Stack>
								)}
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => handleAnswer('no')} color="inherit">
					Not interested
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ProSurveyModal;
